import { useCallback } from 'react';
import { Router } from '../types';
import { AUTH_REDIRECT_COMPLETED_FLAG } from '../constants';
import { useAuthStore } from '../stores';

/**
 * App state passed through Auth0 redirect flow.
 * Contains the return URL to navigate to after token renewal.
 */
export interface AuthRedirectAppState {
    returnTo?: string;
}

/**
 * Parameters for useAuthRedirect hook
 */
export interface UseAuthRedirectParams {
    /**
     * Router instance for navigation
     */
    router: Router;
    /**
     * Path to redirect to when user has selected an organization.
     * Defaults to '/'.
     */
    homePath?: string;
    /**
     * Path to redirect to when user hasn't selected an organization.
     * Defaults to '/organization/select'.
     */
    selectOrgPath?: string;
}

/**
 * Result of useAuthRedirect hook
 */
export interface UseAuthRedirectResult {
    /**
     * Callback to handle Auth0 redirect after authentication.
     * Receives optional appState containing the return URL.
     */
    onRedirectCallback: (appState?: AuthRedirectAppState) => void;
}

/**
 * Custom hook to handle Auth0 redirect logic.
 * This hook determines where to redirect the user after successful authentication
 * based on whether they have already selected an organization.
 *
 * Uses the Zustand auth store to check the selected organization.
 * Supports appState.returnTo to redirect users back to their original
 * page after token renewal on page refresh.
 *
 * @param params - Configuration for the redirect behavior
 * @returns Object with onRedirectCallback function
 *
 * @example
 * ```tsx
 * const { onRedirectCallback } = useAuthRedirect({
 *   router,
 * });
 *
 * return (
 *   <Auth0Provider onRedirectCallback={onRedirectCallback}>
 *     {children}
 *   </Auth0Provider>
 * );
 * ```
 */
export const useAuthRedirect = ({
    router,
    homePath = '/',
    selectOrgPath = '/organization/select',
}: UseAuthRedirectParams): UseAuthRedirectResult => {
    // Get state and actions from Zustand store
    const selectedOrgId = useAuthStore((state) => state.selectedOrgId);
    const pendingOrgId = useAuthStore((state) => state.pendingOrgId);

    const onRedirectCallback = useCallback(
        (appState?: AuthRedirectAppState) => {
            // Mark that the user just completed an Auth0 redirect round-trip.
            // Consumed by useAuthOrganizationValidation to avoid forcing a
            // token renewal right after login.
            sessionStorage.setItem(AUTH_REDIRECT_COMPLETED_FLAG, 'true');

            // Priority 1: If we have a pending org from query param, go to organization/select
            // It will be validated there and navigate directly to home if valid
            // (See the OrganizationSelect feature - Case 2 now navigates directly to home)
            if (pendingOrgId) {
                router.navigate(selectOrgPath, { replace: true });
                return;
            }

            // Priority 2: If we have a selected org in store, use it
            if (selectedOrgId) {
                // If appState contains a returnTo path (from page refresh
                // token renewal), navigate back to that page.
                // Otherwise, navigate to home.
                const returnTo = appState?.returnTo;
                router.navigate(returnTo ?? homePath, { replace: true });
                return;
            }

            // Priority 3: No org selected, go to organization/select
            router.navigate(selectOrgPath, { replace: true });
        },
        [router, selectedOrgId, pendingOrgId, homePath, selectOrgPath],
    );

    return { onRedirectCallback };
};

export default useAuthRedirect;
